import * as React from 'react';
import { cn } from '../lib/cn';
import { chartColors } from '../design-system/tokens/chartColors';

/**
 * ChartStack — part-to-whole, as a 100% stacked bar.
 *
 * ── This is the component that exists INSTEAD of a pie ───────────────────────
 * In a stack only NEIGHBOURING segments touch, so each colour has to be
 * distinguishable from two others. In a pie every slice meets every other slice
 * around the centre, so each has to be distinguishable from ALL of them. That
 * is a much harder test and the palette does not survive it: the same four
 * colours pass here (worst neighbouring pair ΔE 8.0 simulated, 17.0 normal) and
 * fail as a pie (violet against blue, ΔE 2.0 under deuteranopia — the same
 * colour). See chartColors.ts for the validator output.
 *
 * Length also beats angle as a judgement, long category names have somewhere to
 * live, and a 3% segment is a visible sliver here where it is an 11° splinter
 * in a pie.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * HARD CAP OF 4 SERIES. A 5th is folded into "Other" rather than given a
 * generated hue — a generated hue is indistinguishable from an existing one
 * under simulation by construction, so it is never the answer (DC-2).
 *
 * The legend is not decoration. It carries the values, which is what lets the
 * segments stay unlabelled, and it doubles as the readable-another-way relief
 * the contrast check requires.
 */

const MAX_SERIES = 4;

export interface ChartStackDatum {
  label: string;
  value: number;
}

export interface ChartStackProps {
  data: ChartStackDatum[];
  /** Formats each legend value. Defaults to a plain locale number. */
  format?: (value: number) => string;
  /** Show each segment's share of the total beside its value. Default true. */
  showPercent?: boolean;
  /** Describes the composition in words for a screen reader. Required. */
  ariaLabel: string;
  className?: string;
}

export function ChartStack({
  data,
  format = (v) => v.toLocaleString(),
  showPercent = true,
  ariaLabel,
  className,
}: ChartStackProps) {
  const positive = data.filter((d) => d.value > 0);

  // Fold the tail rather than grow the palette. Sorting first means "Other" is
  // genuinely the smallest remainder and not an arbitrary set.
  const sorted = [...positive].sort((a, b) => b.value - a.value);
  const shown =
    sorted.length <= MAX_SERIES
      ? sorted
      : [
          ...sorted.slice(0, MAX_SERIES - 1),
          {
            label: 'Other',
            value: sorted.slice(MAX_SERIES - 1).reduce((s, d) => s + d.value, 0),
          },
        ];

  const total = shown.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;

  return (
    <div className={cn('w-full', className)}>
      {/* 2px gaps so adjacent fills never fuse into one block. The gap is the
          page background showing through, which is why it reads as a division
          rather than as a fifth colour. */}
      <div role="img" aria-label={ariaLabel} className="flex h-[30px] gap-0.5 overflow-hidden rounded-sm">
        {shown.map((d, i) => (
          <span
            key={`${d.label}-${i}`}
            className="block h-full"
            style={{
              width: `${(d.value / total) * 100}%`,
              backgroundColor: chartColors.categorical[i] ?? chartColors.categorical[0],
            }}
          />
        ))}
      </div>

      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
        {shown.map((d, i) => (
          <li key={`legend-${d.label}-${i}`} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ backgroundColor: chartColors.categorical[i] ?? chartColors.categorical[0] }}
            />
            {d.label}
            {/* Values in ink, beside the mark — never set on the fill (DC-7). */}
            <b className="pl-3.5 font-semibold tabular-nums text-foreground">
              {format(d.value)}
              {showPercent && ` · ${Math.round((d.value / total) * 100)}%`}
            </b>
          </li>
        ))}
      </ul>
    </div>
  );
}
